import { ShareRequestStatus } from '@/constants/graphql-enums';
import { CurrentShares } from 'src/modals/ManageSharingModal/queries/ShareByEmail.mutation';

import type { RevokeShareAccess } from 'src/modals/ManageSharingModal/queries/RevokeShareAccess.mutation';
import type { ShareByDomain } from 'src/modals/ManageSharingModal/queries/ShareByDomain.mutation';
import type { ShareByEmails } from 'src/modals/ManageSharingModal/queries/ShareByEmail.mutation';
import type { UpdateShareRequest } from 'src/modals/ManageSharingModal/queries/updateShareRequest.mutation';
import type { Cache } from '@urql/exchange-graphcache';
import type { ResultOf, VariablesOf } from 'gql.tada';

/**
 * Replay a transform over every cached `shares` query using each query's *real*
 * stored args. Hand-built variables only match graphcache's query key if they're
 * byte-identical — they weren't (the `requester_type` filter was dropped), so
 * updates silently missed and the list leaned on refetches to self-correct.
 *
 * The transform receives each query's stored `variables` so it can gate on the
 * query's filter. Each cached `shares` query is scoped to one resource via its
 * `type_id` filter, so pushes MUST check `sharesQueryTargets` — otherwise a share
 * created for one resource leaks into another resource's cached list until refetch.
 */
const updateCachedSharesQueries = (
  cache: Cache,
  transform: (
    data: ResultOf<typeof CurrentShares> | null,
    variables: VariablesOf<typeof CurrentShares>,
  ) => ResultOf<typeof CurrentShares> | null,
) => {
  cache
    .inspectFields('Query')
    .filter(field => field.fieldName === 'shares')
    .forEach(field => {
      const variables = field.arguments as VariablesOf<typeof CurrentShares>;
      cache.updateQuery<ResultOf<typeof CurrentShares>, VariablesOf<typeof CurrentShares>>(
        { query: CurrentShares, variables },
        data => transform(data, variables),
      );
    });
};

/**
 * True if a cached `shares` query targets the given resource. `type_id` uniquely
 * identifies the resource, so a share should only be pushed into queries whose
 * filter matches it. Falls back to `true` when either side is unknown, so a
 * differently-shaped (unfiltered) query still receives the node.
 */
const sharesQueryTargets = (variables: VariablesOf<typeof CurrentShares>, typeId: string | null | undefined) => {
  const filterTypeId = variables?.filter?.type_id?.eq;
  if (filterTypeId == null || typeId == null) return true;
  return filterTypeId === typeId;
};

export const shareMutationUpdates = {
  updateShareRequest(
    result: ResultOf<typeof UpdateShareRequest>,
    args: VariablesOf<typeof UpdateShareRequest>,
    cache: Cache,
  ) {
    const updated = result.updateShareRequest;
    if (updated?.status !== ShareRequestStatus.Rejected && updated?.status !== ShareRequestStatus.Approved) {
      return;
    }

    cache.invalidate({ __typename: 'ShareRequest', _id: args.input._id });

    const share = updated.share;
    if (updated.status === ShareRequestStatus.Approved && share) {
      updateCachedSharesQueries(cache, (data, variables) => {
        if (data?.shares?.nodes && sharesQueryTargets(variables, updated.type_id)) data.shares.nodes.push(share);
        return data;
      });
    }
  },

  shareByEmails(result: ResultOf<typeof ShareByEmails>, args: VariablesOf<typeof ShareByEmails>, cache: Cache) {
    updateCachedSharesQueries(cache, (data, variables) => {
      if (data?.shares?.nodes && result.shareByEmails && sharesQueryTargets(variables, args.input.type_id)) {
        data.shares.nodes.push(...result.shareByEmails);
      }
      return data;
    });
  },

  createShare(result: ResultOf<typeof ShareByDomain>, _args: VariablesOf<typeof ShareByDomain>, cache: Cache) {
    const share = result.createShare;
    if (!share) return;
    updateCachedSharesQueries(cache, (data, variables) => {
      if (data?.shares?.nodes && sharesQueryTargets(variables, share.type_id)) data.shares.nodes.push(share);
      return data;
    });
  },

  revokeShare(_result: ResultOf<typeof RevokeShareAccess>, args: VariablesOf<typeof RevokeShareAccess>, cache: Cache) {
    updateCachedSharesQueries(cache, data => {
      if (data?.shares?.nodes) {
        data.shares.nodes = data.shares.nodes.filter(node => node?._id !== args.id);
      }
      return data;
    });
  },
};
